import type { ForumTagDto } from '@lagerfeuer/shared';
import { ShieldCheck } from 'lucide-react';
import { EmojiText } from '../../components/EmojiText';
import { MAX_TAGS_PER_POST } from '../threads/threadUtils';
import './forum.css';

/** Anzeige eines Forum-Tags (Emoji + Name). */
export function TagChip({ tag, small }: { tag: Pick<ForumTagDto, 'name' | 'emoji' | 'moderated'>; small?: boolean }) {
  return (
    <span className={`forum-tag ${small ? 'small' : ''}`} data-testid="forum-tag">
      {tag.emoji && <EmojiText value={tag.emoji} size={14} />}
      <span>{tag.name}</span>
    </span>
  );
}

/**
 * Mehrfachauswahl von Tags. Moderierte Tags dürfen nur mit MANAGE_THREADS gesetzt/entfernt werden
 * (Backend prüft das ebenso); bereits gesetzte moderierte Tags bleiben für andere unverändert.
 */
export function TagPicker({
  tags,
  selected,
  onChange,
  isMod,
  initial = [],
  max = MAX_TAGS_PER_POST,
}: {
  tags: ForumTagDto[];
  selected: string[];
  onChange: (ids: string[]) => void;
  isMod: boolean;
  initial?: string[];
  max?: number;
}) {
  const sorted = [...tags].sort((a, b) => a.position - b.position);
  const full = selected.length >= max;
  return (
    <div className="forum-tag-picker" role="group" aria-label={`Tags (höchstens ${max})`} data-testid="tag-picker">
      {sorted.map((t) => {
        const on = selected.includes(t.id);
        const lockedByMod = t.moderated && !isMod;
        const disabled = lockedByMod || (!on && full);
        return (
          <button
            key={t.id}
            type="button"
            className="forum-tag-toggle"
            aria-pressed={on}
            disabled={disabled}
            title={lockedByMod ? (initial.includes(t.id) ? 'Moderierter Tag – nur Moderierende können ihn entfernen' : 'Moderierter Tag – nur Moderierende können ihn setzen') : undefined}
            onClick={() => onChange(on ? selected.filter((x) => x !== t.id) : [...selected, t.id])}
            data-testid="tag-picker-tag"
            data-tag-id={t.id}
          >
            {t.emoji && <EmojiText value={t.emoji} size={16} />}
            <span>{t.name}</span>
            {t.moderated && <ShieldCheck size={12} aria-label="moderiert" role="img" />}
          </button>
        );
      })}
      {sorted.length === 0 && <span className="small faint">Dieses Forum hat keine Tags.</span>}
    </div>
  );
}
